import type { ChangeEvent } from "react";
import { useEffect, useState } from "react";

import type { Dispatch } from "@reduxjs/toolkit";

import { useCheckoutConfiguration } from "@jti/commerce/providers";
import { toast } from "@jti/core-components/components/Toaster";
import { debounce } from "@jti/fe-common";
import type { Product } from "@jti/magento";
import { getSessionStorage } from "@jti/rest-api";
import { type GetEligibleCampaignsResponseType } from "@jti/rest-api-types";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import type { Loyalty } from "@jti/store/slices/loyaltySlice";
import { applyCampaign, loyaltySlice, removeCampaign } from "@jti/store/slices/loyaltySlice";

import { useLoyaltyProductPoints } from "@checkout/hooks/useLoyaltyProductPoints";
import getSelectedVoucher from "@checkout/utils/getSelectedVoucher";

export type UseLoyaltyValue = {
  cartPoints: number;
  userPoints: number;
  loyaltyChoice: 0 | 1 | undefined;
  vouchers: GetEligibleCampaignsResponseType[];
  isQueryCampaign: boolean;
  isLoyaltyError: boolean;
  loyaltyCampaignId: string;
  maxVoucherValue: number;
  minVoucherCost: number;
  setCollect: () => void;
  setRedeem: () => void;
  handleCampaignChange: (arg0: ChangeEvent<HTMLInputElement> | string) => Promise<void>;
  highestAvailableVoucher: GetEligibleCampaignsResponseType | number;
  hasCampaignSelected: boolean;
  redemptionTabSymbol: string;
};

export type UserSelection = {
  loyaltyChoice: LOYALTY_CHOICE;
  cartId: string;
};

export enum LOYALTY_CHOICE {
  COLLECT = 0,
  REDEEM = 1,
}

const getMinVoucherCost = (vouchers: GetEligibleCampaignsResponseType[]) =>
  vouchers?.find(({ is_eligible }: GetEligibleCampaignsResponseType) => is_eligible)?.points || 0;
const findVoucher = (vouchers: GetEligibleCampaignsResponseType[], cartSubTotal: number) =>
  vouchers?.find((voucher) => voucher.price >= cartSubTotal)?.price || cartSubTotal | 0;

const clearSelection = (dispatch: Dispatch<any>, cartId: string) => {
  if (!cartId) {
    return;
  }

  dispatch(removeCampaign(cartId));
};

const setLoyaltyCartPoints = (cartItems: Product[], userSelection: UserSelection, cartPoints: number) => {
  if (cartItems?.length) {
    const loyaltyCartPoints = userSelection.loyaltyChoice === LOYALTY_CHOICE.REDEEM ? 0 : cartPoints;
    getSessionStorage().set("loyaltyCartPoints", loyaltyCartPoints);
  }
};

const setCampaignErrors = debounce((campaignErrors: Loyalty["campaignErrors"]) => {
  if (campaignErrors.length <= 1) {
    campaignErrors.forEach((error) => {
      toast.error(error.message);
    });
  }
}, 500);

const handleVoucherErrors = (
  dispatch: Dispatch<any>,
  selectedVoucher: GetEligibleCampaignsResponseType | undefined,
  userPoints: number,
  highestAvailableVoucher: number,
  cartId: string
) => {
  if (selectedVoucher && (selectedVoucher.points > userPoints || selectedVoucher.price > highestAvailableVoucher)) {
    dispatch(loyaltySlice.actions.setLoyaltyError(true));
    clearSelection(dispatch, cartId);
  }
};

const clearLoyaltyStorage = (cartItems: Product[]) => {
  if (cartItems?.length === 0) {
    getSessionStorage().delete("loyaltyChoice");
  }
};

const updateUserSelection = (choice: LOYALTY_CHOICE, cartId: string) => {
  getSessionStorage().set("loyaltyChoice", {
    loyaltyChoice: choice,
    cartId,
  });
};

export const useLoyalty = (): UseLoyaltyValue => {
  const [isQueryCampaign, setIsQueryCampaign] = useState(false);
  const [hasCampaignSelected, setHasCampaignSelected] = useState(false);
  const userSelection = getSessionStorage().get("loyaltyChoice", {
    loyaltyChoice: 0 as LOYALTY_CHOICE,
    cartId: "" as string,
  });
  const {
    loyaltyChoice,
    points: userPoints,
    vouchers,
    isLoyaltyError,
    loyaltyCampaignId,
    campaignErrors = [],
  } = useSelector((store) => store.loyalty);
  const dispatch = useDispatch();
  const cartId: string = useSelector((store) => store.cart.id as string);
  const checkoutConfiguration = useCheckoutConfiguration();
  const cartSubTotal: number = useSelector((store) => store.cart.prices.data?.subtotal?.value ?? 0);
  const cartItems: Product[] = useSelector((store) => store.cart.items);
  const itemsPending: boolean = useSelector((store) => store.cart.itemsPending);

  const cartPoints = useLoyaltyProductPoints(cartItems);

  const highestAvailableVoucher: number = findVoucher(vouchers, cartSubTotal);
  const availableVouchers = vouchers.filter((voucher) => voucher.is_eligible);
  const maxVoucherValue = availableVouchers[availableVouchers.length - 1]?.price ?? 0;
  const minVoucherCost = getMinVoucherCost(vouchers);

  const redemptionTabSymbol = checkoutConfiguration.loyalty?.redemptionTabSymbol || "%";

  const setCollect = () => {
    dispatch(loyaltySlice.actions.setLoyaltyChoice(LOYALTY_CHOICE.COLLECT));
    updateUserSelection(LOYALTY_CHOICE.COLLECT, cartId as string);

    if (!cartId) {
      return;
    }

    dispatch(removeCampaign(cartId));
  };

  const setRedeem = () => {
    dispatch(loyaltySlice.actions.setLoyaltyChoice(LOYALTY_CHOICE.REDEEM));
    updateUserSelection(LOYALTY_CHOICE.REDEEM, cartId as string);
  };

  const handleApplyCampaign = async (campaginUuid: string) => {
    if (!cartId) {
      return;
    }

    try {
      await dispatch(applyCampaign({ cartId, campaign_uuid: campaginUuid }));
    } catch {
      dispatch(loyaltySlice.actions.setLoyaltyError(true));
    }
  };

  const handleCampaignChange = async (input: ChangeEvent<HTMLInputElement> | string): Promise<void> => {
    setIsQueryCampaign(true);
    dispatch(loyaltySlice.actions.setLoyaltyError(false));
    const campaignId = typeof input === "string" ? input : input.target.value;
    await handleApplyCampaign(campaignId);

    setIsQueryCampaign(false);
  };

  useEffect(() => {
    const loyaltyChoice =
      cartId && userSelection.cartId === cartId && availableVouchers.length
        ? userSelection.loyaltyChoice
        : LOYALTY_CHOICE.COLLECT;

    dispatch(loyaltySlice.actions.setLoyaltyChoice(loyaltyChoice));
  }, [availableVouchers?.length, cartId, dispatch, userSelection.cartId, userSelection.loyaltyChoice, cartPoints]);

  useEffect(() => {
    setLoyaltyCartPoints(cartItems, userSelection, cartPoints);
  }, [cartPoints, userSelection.loyaltyChoice]);

  useEffect(() => {
    const selectedVoucher = getSelectedVoucher(vouchers, loyaltyCampaignId);

    const selectedCampaign = (!selectedVoucher && userSelection.loyaltyChoice === LOYALTY_CHOICE.REDEEM) || true;
    setHasCampaignSelected(selectedCampaign);

    handleVoucherErrors(dispatch, selectedVoucher, userPoints, highestAvailableVoucher, cartId);
    clearLoyaltyStorage(cartItems);
  }, [cartItems?.length, cartSubTotal, loyaltyCampaignId, userPoints, vouchers?.length, userSelection.loyaltyChoice]);

  useEffect(() => {
    setCampaignErrors(campaignErrors);
  }, [campaignErrors]);

  useEffect(() => {
    if (!itemsPending && loyaltyCampaignId && loyaltyChoice === LOYALTY_CHOICE.COLLECT) {
      dispatch(loyaltySlice.actions.setLoyaltyChoice(LOYALTY_CHOICE.REDEEM));
      updateUserSelection(LOYALTY_CHOICE.REDEEM, cartId);
    }
  }, [itemsPending, loyaltyCampaignId]);

  return {
    cartPoints,
    loyaltyChoice,
    userPoints,
    vouchers,
    isQueryCampaign,
    loyaltyCampaignId,
    isLoyaltyError,
    maxVoucherValue,
    minVoucherCost,
    setCollect,
    setRedeem,
    handleCampaignChange,
    highestAvailableVoucher,
    hasCampaignSelected,
    redemptionTabSymbol,
  };
};
